import BlogPostLayout from '../components/BlogPostLayout';
import type { BlogPost } from '../components/BlogPostLayout';

const post: BlogPost = {
  slug: 'geborduurde-tshirts-inkopen-china',
  title: 'Geborduurde T-shirts inkopen uit China: tot 300% marge op 250g katoen',
  metaDescription: 'Geborduurde T-shirts inkopen uit China? Leer hoe je 250g katoenen shirts met borduurwerk inkoopt voor €6-9 en verkoopt voor €25-35. DUC regelt alles.',
  metaKeywords: 'geborduurde t-shirts inkopen China, t-shirts laten maken China, private label kleding China, merchandise groothandel',
  heroImage: 'https://storage.readdy-site.link/project_files/858ea215-68cb-4d8e-b2ba-23583e2fd22e/005aff3f-6650-4614-a607-26ddd6659aa1_Geborduurde-T-shirts.png?v=9229927384bb88bb0001a1c9cb51e92e',
  heroImageAlt: 'Geborduurde T-shirts inkopen uit China via DUC sourcing agent',
  category: 'Mode & Merchandise',
  readTime: '8 min',
  inkoopprijs: '€6 – €9',
  verkoopprijs: '€25 – €35',
  marge: 'Tot 300%',
  productLabel: 'Geborduurde T-shirts',
  intro: 'Jij wil een sterk merk neerzetten of je winst verhogen — maar je betaalt nu te veel voor je merchandise. Geborduurde T-shirts van 250g katoen inkopen uit China is de slimste zet die je kunt maken.',
  sections: [
    {
      id: 'wat-is-het',
      heading: 'Wat zijn geborduurde T-shirts van 250g katoen?',
      subheading: 'Het verschil tussen goedkoop en kwalitatief',
      content: [
        'Een 250g katoenen T-shirt is het standaard gewicht dat je terugvindt in kwalitatieve A-merken. Het voelt zwaarder, oogt waardevoller en gaat langer mee dan de typische 160g shirts die je bij bulkleveranciers ziet.',
        'Borduurwerk geeft een T-shirt direct een premium uitstraling. In tegenstelling tot opdruk (print) vervaagt borduurwerk nooit — het gaat mee voor de levensduur van het shirt. Voor merken en bedrijfskleding is dit cruciaal.',
        'Dit product verkoopt zichzelf: het ziet er goed uit, het voelt duur aan en het communiceert kwaliteit zonder dat jij er veel bij hoeft te vertellen.',
      ],
    },
    {
      id: 'waarom-verkoopt',
      heading: 'Waarom verkoopt dit product goed?',
      subheading: 'Structurele vraag, niet een hype',
      content: [
        '<strong>Bedrijven</strong> kopen altijd bedrijfskleding. Horeca, bouw, sportclubs, winkels — iedereen heeft werkkleding nodig. Geborduurde shirts zijn de standaard voor een professionele uitstraling.',
        '<strong>Webshops en merken</strong> zien merchandise als gratis marketing. Klanten die jouw shirt dragen zijn wandelende reclame. De vraag naar kwalitatieve merkshirts groeit elk jaar.',
        '<strong>Events en promotie</strong>: festivals, congressen, sportteams — bij elk event willen organisatoren shirts met logo. De MOQ (minimale bestelhoeveelheid) past perfect bij eventvolumes.',
        'Dit is geen hype. Kleding en merchandise worden altijd afgenomen. Het is een van de meest stabiele productcategorieën voor herhaalorders.',
      ],
      highlight: [
        { label: 'Doelgroepen', value: '10+' },
        { label: 'Herhaalorders', value: 'Hoog' },
        { label: 'Min. bestelling', value: 'v.a. 50 st.' },
        { label: 'Levertijd', value: '3–5 wk' },
      ],
    },
    {
      id: 'markt-nederland',
      heading: 'De markt in Nederland',
      content: [
        'Een kwalitatief geborduurde shirt kost in Nederland gemakkelijk €25 tot €35 bij leveranciers als Bedrukkerij.nl of Shirtstore. Zakelijke klanten bestellen routinematig 100-500 stuks en vragen specifieke specificaties.',
        'Op platforms als Bol.com scoren gepersonaliseerde en geborduurde shirts hoog in zoekresultaten. Er is weinig prijs-transparantie — klanten weten niet wat de inkoopprijs is, ze beoordelen op kwaliteit en uitstraling.',
        'In de B2B-markt zijn raamcontracten normaal. Eén klant die jaarlijks herbestelt, is een goudmijn voor jouw cashflow.',
      ],
      tip: 'Richt je niet op losse consumenten maar op bedrijven. Eén kantoor van 20 medewerkers die 3x per jaar bestelt = structurele inkomsten met hoge marge.',
    },
    {
      id: 'winstpotentie',
      heading: 'Winstpotentie: wat verdien je per shirt?',
      content: [
        'Laten we de cijfers eerlijk bekijken:',
        '— Inkoopprijs China (incl. borduurwerk, DDP levering NL): <strong>€6 – €9 per stuk</strong><br />— Verkoopprijs Nederland: <strong>€25 – €35 per stuk</strong><br />— Brutomarge per shirt: <strong>€16 – €26</strong>',
        'Op een order van 200 shirts maak je al snel €3.200 tot €5.200 brutomarge. Trek je kosten eraf (opslag, verpakking, verkooptijd), dan houd je een stevige nettomarge over.',
        'Wil je private label? Jouw eigen label in de kraag kost vrijwel niets extra — maar verhoogt de perceived value aanzienlijk. Klanten betalen meer voor een "eigen merk" shirt.',
      ],
      highlight: [
        { label: 'Inkoop / stuk', value: '€6–9' },
        { label: 'Verkoop / stuk', value: '€25–35' },
        { label: 'Brutomarge', value: 'tot 300%' },
        { label: 'Per order (200 st)', value: '±€4.000' },
      ],
    },
    {
      id: 'waarom-china',
      heading: 'Waarom inkopen uit China slim is voor dit product',
      content: [
        'China heeft de meest ontwikkelde textielsector ter wereld. Fabrieken in provincies als Guangdong en Zhejiang produceren voor dezelfde A-merken die in Europa voor €40+ per shirt worden verkocht.',
        'Door <strong>direct bij de fabriek</strong> in te kopen betaal je geen importeur, geen distributeur en geen wederverkoper. Elk tussenstation dat je overslaat, is marge die in jouw zak blijft.',
        'Belangrijker: je kunt specificaties bepalen. Wil je 240g in plaats van 250g? Een specifieke kleur Pantone? Een bepaald borduurpatroon? In China is customization de standaard, geen uitzondering.',
        '<strong>Minimum bestelhoeveelheid</strong> voor geborduurde shirts ligt typisch op 50-100 stuks per kleur/design. Dat is voor elke serieuze ondernemer haalbaar.',
        'Wil je precies begrijpen waarom <a href="/goedkoop-inkopen-china" class="text-[#3B9EE8] font-semibold underline underline-offset-2 hover:text-[#2D85C5]">goedkoop inkopen uit China</a> zo aantrekkelijk is en hoe je de beste fabrieksprijzen krijgt? Lees ons uitgebreide overzicht met alle ins en outs.',
      ],
    },
    {
      id: 'risicos',
      heading: 'Risico\'s als je dit zelf probeert te regelen',
      content: [
        'Zonder ervaring loop je tegen stevige problemen aan:',
        '<strong>Kwaliteitsverschillen:</strong> Fabrieken op Alibaba tonen hun beste samples — de productie is soms een andere story. Kleur kan afwijken, borduurwerk kan loslaten na 3 wassen.',
        '<strong>Te duur inkopen:</strong> Zonder kennis van marktprijzen en zonder directe fabriekscontacten betaal je handelaarprijzen in plaats van fabrieksprijzen.',
        '<strong>Leverproblemen:</strong> Douane, invoerrechten, vertragingen bij de haven — wie dit niet kent, loopt vast. Zeker met grote orders die tijdgevoelig zijn.',
        '<strong>Communicatieproblemen:</strong> Chinese fabrieken werken snel en verwachten duidelijke specificaties. Onduidelijkheid = fouten in de productie.',
      ],
    },
    {
      id: 'hoe-duc-helpt',
      heading: 'Hoe DUC dit voor jou regelt',
      content: [
        'DUC neemt het volledige traject uit handen:',
        '✔ <strong>Leveranciersselectie:</strong> Wij selecteren alleen geverifieerde fabrieken die eerder voor Europese merken hebben geproduceerd.<br />✔ <strong>Onderhandeling:</strong> Wij kennen de juiste prijzen. Geen oplichting, geen opgeblazen tarieven.<br />✔ <strong>Kwaliteitscontrole:</strong> Steekproeven voor en na productie. Borduurkwaliteit, naadsterkte, kleurechtheid.<br />✔ <strong>DDP levering:</strong> Alles-in prijs, aan jouw deur in Nederland. Geen verrassingen bij de douane.<br />✔ <strong>Aftercare:</strong> Herhaalorders, aanpassingen, nieuwe designs — wij blijven beschikbaar.',
        'Jij stuurt ons een beschrijving, foto of sample. Wij sturen binnen 24 uur een gratis prijsindicatie. Geen verplichtingen.',
      ],
    },
    {
      id: 'hoe-klanten',
      heading: 'Hoe krijg jij klanten voor geborduurde T-shirts?',
      content: [
        '<strong>B2B directe benadering</strong> werkt het beste. Stap naar horecazaken, sportclubs, bouwbedrijven en kantoren in je regio met samples. Eén handdruk en een sample in de hand converteert beter dan welke advertentie ook.',
        '<strong>Bol.com & Amazon:</strong> Zoek op "bedrijfsshirts" of "geborduurde T-shirts" — je ziet meteen de concurrentie. De meeste listings zijn slecht geoptimaliseerd. Ruimte genoeg.',
        '<strong>Instagram & LinkedIn:</strong> Toon het product in gebruik. Een foto van een team in matching shirts bij hun zaak deelt organisch en trekt soortgelijke bedrijven aan.',
        '<strong>Eigen webshop + Google:</strong> Target longtail keywords zoals "geborduurde shirts bestellen kleine oplage" of "bedrijfskleding borduren snel".',
      ],
    },
  ],
  faqs: [
    { q: 'Wat is de minimale bestelhoeveelheid voor geborduurde T-shirts uit China?', a: 'De meeste fabrieken hanteren een MOQ van 50-100 stuks per kleur en design. DUC zoekt altijd een fabriek die past bij jouw gewenste volume.' },
    { q: 'Hoe lang duurt het voor mijn shirts geleverd worden?', a: 'Inclusief productie (2-3 weken) en zeevracht (3-4 weken) reken je op 6-8 weken. Via luchtvracht is dit 1-2 weken extra.' },
    { q: 'Kan ik mijn eigen label in de shirts laten zetten?', a: 'Ja. Private label met jouw eigen merknaam in de kraag is standaard beschikbaar. Weinig meerkosten, grote impact op de perceived value.' },
    { q: 'Zijn de shirts gecertificeerd voor EU-normen?', a: 'DUC werkt alleen met fabrieken die OEKO-TEX of vergelijkbare certificeringen kunnen leveren. EU-conformiteit is standaard.' },
  ],
  ctaTitle: 'Wil jij starten met geborduurde T-shirts?',
  ctaText: 'Stuur ons je specificaties of een referentieproduct. Binnen 24 uur ontvang je een gratis, vrijblijvende prijsindicatie — inclusief inkoopprijs, leveringstijd en marge-indicatie.',
  relatedPosts: [
    { title: 'Luxe bedrukte paper bags inkopen', href: '/blog/bedrukte-paper-bags-inkopen-china', label: 'Verpakkingen' },
    { title: 'E-choppers inkopen uit China', href: '/blog/e-choppers-inkopen-china', label: 'E-mobiliteit' },
    { title: 'Bedrukte tissues voor horeca', href: '/blog/bedrukte-tissues-horeca-inkopen-china', label: 'Horeca' },
  ],
};

export default function GeborduurdeShirtsPage() {
  return <BlogPostLayout post={post} />;
}
